import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@suscri/ui';
import { ArrowRight, KeyRound, Mail, UserRound } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import {
  AuthShell,
  authErrorClass,
  authInputClass,
  authLabelClass,
} from '../components/AuthShell';
import { useAuthStore } from '../lib/auth';
import { trpc } from '../lib/trpc';
import { getLandingUrl } from '../lib/urls';

const registerSchema = z
  .object({
    name: z.string().min(2, 'El nombre debe tener al menos 2 caracteres').optional(),
    email: z.string().email('Email inválido'),
    password: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres'),
    confirmPassword: z.string(),
    acceptTerms: z.literal(true, {
      errorMap: () => ({ message: 'Debes aceptar los términos y la política de privacidad' }),
    }),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  });

type RegisterForm = z.infer<typeof registerSchema>;

export function RegisterPage() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  const registerMutation = trpc.auth.register.useMutation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<RegisterForm>({ resolver: zodResolver(registerSchema) });

  const onSubmit = async (data: RegisterForm) => {
    setIsLoading(true);
    try {
      const { confirmPassword, ...registerData } = data;
      const result = await registerMutation.mutateAsync({
        ...registerData,
        name: registerData.name ?? '',
      });
      setAuth(result.user, result.token);
      navigate('/dashboard');
    } catch (error) {
      setError('root', {
        message: error instanceof Error ? error.message : t('Error al crear la cuenta'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell
      title={t('Crear cuenta')}
      subtitle={
        <>
          {t('¿Ya tienes una cuenta?')}{' '}
          <Link to="/login" className="font-semibold text-primary hover:text-primary/80">
            {t('Iniciar sesión')}
          </Link>
        </>
      }
      footer={
        <a href={getLandingUrl()} className="font-medium hover:text-foreground">
          {t('Volver a la página principal')}
        </a>
      }
    >
      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="grid gap-5 sm:grid-cols-2">
          <AuthField
            id="name"
            label={t('Nombre (opcional)')}
            icon={UserRound}
            error={errors.name?.message}
          >
            <input {...register('name')} id="name" type="text" autoComplete="name" className={`${authInputClass} pl-10`} placeholder={t('Tu nombre')} />
          </AuthField>
          <AuthField id="email" label="Email" icon={Mail} error={errors.email?.message}>
            <input {...register('email')} id="email" type="email" autoComplete="email" className={`${authInputClass} pl-10`} placeholder="tu@email.com" aria-invalid={Boolean(errors.email)} />
          </AuthField>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <AuthField id="password" label={t('Contraseña')} icon={KeyRound} error={errors.password?.message}>
            <input {...register('password')} id="password" type="password" autoComplete="new-password" className={`${authInputClass} pl-10`} placeholder="••••••••" aria-invalid={Boolean(errors.password)} />
          </AuthField>
          <AuthField id="confirmPassword" label={t('Confirmar contraseña')} icon={KeyRound} error={errors.confirmPassword?.message}>
            <input {...register('confirmPassword')} id="confirmPassword" type="password" autoComplete="new-password" className={`${authInputClass} pl-10`} placeholder="••••••••" aria-invalid={Boolean(errors.confirmPassword)} />
          </AuthField>
        </div>

        <div>
          <label className="flex items-start gap-3 rounded-xl border border-border bg-background/40 p-3.5 text-sm leading-6 text-muted-foreground">
            <input {...register('acceptTerms')} type="checkbox" className="mt-1 h-4 w-4 rounded border-input bg-background text-primary focus:ring-primary" />
            <span>
              {t('Acepto los')}{' '}
              <a href={`${getLandingUrl()}/terminos`} target="_blank" rel="noreferrer" className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80">{t('Términos de Servicio')}</a>{' '}
              {t('y la')}{' '}
              <a href={`${getLandingUrl()}/privacidad`} target="_blank" rel="noreferrer" className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80">{t('Política de Privacidad')}</a>
            </span>
          </label>
          {errors.acceptTerms && <p className={authErrorClass}>{errors.acceptTerms.message}</p>}
        </div>

        {errors.root && (
          <div className="rounded-xl border border-danger/25 bg-danger/10 p-3.5 text-sm font-medium text-danger" role="alert">{errors.root.message}</div>
        )}

        <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
          {isLoading ? t('Creando cuenta...') : t('Crear cuenta')}
          {!isLoading && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
        </Button>
      </form>
    </AuthShell>
  );
}

function AuthField({
  id,
  label,
  icon: Icon,
  error,
  children,
}: {
  id: string;
  label: string;
  icon: typeof Mail;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className={authLabelClass}>{label}</label>
      <div className="relative">
        <Icon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        {children}
      </div>
      {error && <p className={authErrorClass}>{error}</p>}
    </div>
  );
}
